import * as Sentry from "@sentry/react";
import i18n from 'translation';
import { sessionToken, updateSessionToken, currentUser, clearExpiredSession } from "./InfegyUser";
import FetchQueue from './FetchQueue';

// A random cache param value to append to API requests to avoid over-aggressive chrome caches
const randomCacheParam = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

function basicURIEncode(p: string): string {
    // Less aggressive than encodeURIComponent: leave anything we can get away with
    return p.replaceAll(/[?#%&=+]/g, function (c) {
        return '%' + c.charCodeAt(0).toString(16).toUpperCase();
    }).replaceAll(' ', '+');
}

export async function readStreamAsText(readableStream: ReadableStream<Uint8Array>): Promise<string> {
    const reader = readableStream.getReader();
    const decoder = new TextDecoder();
    let result = '';

    try {
        while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        result += decoder.decode(value, { stream: true });
        }
        // Final decode to flush any remaining characters
        result += decoder.decode();
        return result;
    } catch (error) {
        Sentry.captureException(error);
        throw error;
    }
}

export class APIError extends Error {
    readonly code: number;
    readonly endpoint: string;
    readonly body?: any;
    readonly server_message?: string;

    constructor(_message: string | undefined, _status_code: number, _endpoint: string, _body?: any, _server_message?: string) {
        super(_message);
        this.code = _status_code;
        this.endpoint = _endpoint;
        this.body = _body;
        this.server_message = _server_message;

        // if error is 404, set message to "Not Found"
        if (_status_code === 404) {
            this.message = i18n.t("api-request.not-found-message");
        }

        // Output this error to the console - can be helpful for backend devs
        console.error("API Request Error from " + _endpoint + "; Code: " + _status_code.toString() + "; Server said: " + (_server_message || "[nothing]"));
    }
}

export function enqueueAPIErrorSnackbar(apiError: APIError, enqueueSnackbar: (message: string, options?: any) => void, customPrefixMessage?: string) {
    enqueueSnackbar(`${customPrefixMessage ? customPrefixMessage + ': ' : ''}${apiError?.message} (${apiError?.code}: ${apiError?.server_message})`, {variant: "error", persist: true});
}

export type APIRequestErrorCallbackFn = (e: APIError) => void;

let APIRequestErrorCallbacks: Array<APIRequestErrorCallbackFn> = [];

export function registerAPIRequestErrorCallback(fn: APIRequestErrorCallbackFn) {
    APIRequestErrorCallbacks.push(fn);
}

export function unregisterAPIRequestErrorCallback(fn: APIRequestErrorCallbackFn) {
    APIRequestErrorCallbacks = APIRequestErrorCallbacks.filter((i: APIRequestErrorCallbackFn) => i !== fn)
}

export function BuildAPIURL(endpoint: string, query_params?: string | { [key: string]: string; }): string {
    if (endpoint.includes('://'))
        return endpoint;

    let apiUrl = import.meta.env.VITE_API_BASE + endpoint;

    if (query_params !== undefined) {
        if (typeof query_params !== "string") {
            // build query_params as a string / expecting it is an object
            let qs = "?";
            for (const [key, value] of Object.entries(query_params)) {
                if (qs.length > 1)
                    qs += "&";
                qs += key;
                if (value !== null && value !== undefined) {
                    qs += "=" + basicURIEncode(value.toString());
                }
            }
            apiUrl += qs;
        } else if (query_params.length > 0) {
            apiUrl += "?" + query_params;
        }
    }

    // Only append cache param for requests to /api/query-info/
    if (apiUrl.includes('/api/query-info/')) {
        apiUrl += (apiUrl.includes('?')) ? "&" : "?";
        apiUrl += "r=" + randomCacheParam;
    }

    return apiUrl;
}

// We will queue queried API requests
// The constructor parameter here limits simultaneous requests to 2
const apiRequestCacheExpiration = 5 * 60 * 1000;
const apiQueryQueue = new FetchQueue(2, apiRequestCacheExpiration);

// Execute a request to the API
// Provide an endpoint to request, such as "me" to fetch /api/me
// query_params is either a pre-built query string (not including a prefixing ?, such as "param1=value1&param2=value2")
//   or an object of string keys to string values, such as { param1: "value1", param2: "value2" }
// init accepts an object of options taken by the fetch() function's init parameter (some may be overridden here)
//
// Example to load an audience-segment object: APIRequest('obj/audience-segment', {id: 'abc123'})
export default async function APIRequest(endpoint: string, query_params?: string | { [key: string]: string; }, init?: RequestInit, bypassCache: boolean = false, allowRetryCount: number = 3, skipSentryLoggingOnStatusCodes: Array<number> = []) {
    const throwAPIError = (_message: string | undefined, _status_code: number, _body?: any, _server_message?: string, sentry_extras?:  any) => {
        const err = new APIError(_message, _status_code, endpoint, _body, _server_message);
        if (_status_code && !skipSentryLoggingOnStatusCodes.includes(_status_code)) {
            Sentry.captureException(err, {
                level: 'error',
                extra: {
                    body: _body,
                    status_code: _status_code,
                    endpoint: endpoint,
                    method: init?.method || "unspecified",
                    requestInit: init,
                    bypassCache: bypassCache,
                    ...sentry_extras
                }
            });
        }
        APIRequestErrorCallbacks.forEach((fn: APIRequestErrorCallbackFn) => fn(err));
        throw err;
    }

    const apiUrl = BuildAPIURL(endpoint, query_params);

    if (apiUrl.length >= 32768) {
        // If the URI is over 32KB, Cloudflare will reject it
        throwAPIError(i18n.t("api-request.bad-request-message") + " (URI too long)", 414);
    }

    if (init === null || init === undefined) {
        // If no init was passed, initialize it as an empty object, as we will set some options below
        init = {}
    }

    init.redirect = "error";
    init.mode = "cors";

    // This silliness fixes a TypeScript build error
    if (!init.headers) {
        init.headers = new Headers();
    } else {
        init.headers = new Headers(init.headers);
    }

    if (sessionToken()) {
        // Set our session token if we have one (authentication)
        init.headers.set("Authorization", String(sessionToken()));
        init.credentials = "include";
    } else if (endpoint.startsWith('query')) { // e.g. 'query-info', 'query/info', 'query/records', 'query/topics', etc., etc.
        // These endpoints ALWAYS require auth. If we don't have auth, they will always fail
        // If we send too many of these, the API will ban us!
        throwAPIError(i18n.t("api-request.authentication-error-message"), 401);
        if (!apiUrl.includes("login")) {
            window.location.href = "/login";
        }
    }

    if (currentUser()?.preferences?.time_zone) {
        init.headers.set("x-timezone", currentUser()?.preferences?.time_zone);
    } else if(Intl.DateTimeFormat().resolvedOptions().timeZone) {
        // default is to send the browser's timezone
        init.headers.set("x-timezone", Intl.DateTimeFormat().resolvedOptions().timeZone);
    }

    // We'll save this on response for use in the chained JSON parse promise
    let status_code: number = 0;

    // A "query" request is one that sends a query to process, heavier to compute, and quota'd
    const isQueryRequest = endpoint.startsWith('query/');

    let retriesRemaining = allowRetryCount;

    while (retriesRemaining+1 > 0) {
        let result: Response | undefined = undefined;
        let responseBody: string | undefined = undefined;
        try {
            result = await apiQueryQueue.enqueue(apiUrl, init, !isQueryRequest, bypassCache || endpoint.startsWith('heartbeat'));
            status_code = result.status || -1;

            if (status_code === 404) {
                // Endpoint not found
                // This will likely not be JSON
                throwAPIError(i18n.t("api-request.bad-request-message") + " (404 Not Found)", status_code, await result.text());
            } else if (status_code === 414) {
                // 414 is not returned by the API, rather Cloudflare's URI-too-long error
                throwAPIError(i18n.t("api-request.bad-request-message") + " (Cloudflare URI too long)", status_code, await result.text());
            } else if ((status_code >= 520 && status_code <= 523) || status_code == 525 || status_code == 530) {
              // These are recoverable/retryable errors from Cloudflare
              // 520: "web server returns an unknown error" - almost always TLS stream corruption, so we need to make a new connection
              // 521: "web server is down" - The API is down, usually briefly (~5 seconds)
              // 522: "connection timed out" - Rare, but related to 521, possibly the whole server is down and we tried before a handoff/switchover
              // 523: "origin is unreachable" - No route to the API, rare, unexpected network issue
              // 525: "SSL handshake failed" - The SSL handshake failed, likely temporary
              // 530: "Cloudflare is unable to resolve the origin hostname" - likely temporary (unexpected, anyway, as our origin is specified via IP address)
              // So, up to 3 times (by default), we will try the request again, with a 1 second pause
              // We need the browser to reconnect here, which it won't do immediately, making a pause helpful.
              // And why multiple retries? For errors like the API being down, which may take a couple seconds to come back up.
              if (retriesRemaining > 0) {
                console.warn(endpoint, status_code, "API response, will retry...");
                retriesRemaining--;
                await new Promise(r => setTimeout(r, 1000));
                continue;
              } else {
                console.error(`${endpoint} ${status_code} API response, will not retry!`);
                // This message indicates a temporary server problem
                throwAPIError(i18n.t("api-request.parse-error-message"), status_code, await result.text());
              }
            } else if (status_code === 524) {
              // Cloudflare 100-second response timeout, raise a more appropriate status
              console.error(`${endpoint} Cloudflare 524 status (100-second timeout), raising as 413`);
              throwAPIError(i18n.t("api-request.timeout-error-message"), 413);
            } else if (status_code >= 526 && status_code < 530) {
                // These are error codes returned by Cloudflare
                // The result will not be JSON, rather an HTML error page
                throwAPIError(i18n.t("api-request.connection-error-message"), status_code, await result.text());
            } else if(status_code >= 531 && status_code < 540) {
                // The API returns these codes for external AI service failures
                throwAPIError(i18n.t("api-request.external-ai-error-message"), status_code, await result.text());
            }

            const auth_update = result.headers.get("X-Authorization-Update");
            if(auth_update !== null && auth_update?.length) {
                // We got an updated session token!
                updateSessionToken(auth_update);
            }

            if(status_code === 204) {
                // 204 is empty response, but OK!
                // We return an object for consistency
                return {};
            } else {
                if (status_code === 200 && result.url.match(/\?.*format=(csv|tsv)/)) {
                    return result;
                } else if((result.headers.get("Content-Type") || "json").includes("json")) {
                    let parsed = {};

                    try {
                        // Try to parse JSON
                        responseBody = await result.text();

                        // We have an intermittent issue where the API returns a 200 status but an empty body
                        // This code will alert Sentry if that happens, and can be removed if we resolve the issue
                        if (status_code === 200 && (!responseBody || responseBody.length === 0)) {
                            Sentry.captureMessage(`Empty response body with 200 status`, {
                                level: 'error',
                                extra: {
                                    fullAPIResponse: {
                                        url: apiUrl,
                                        headers: Object.fromEntries(result.headers.entries()),
                                        status: status_code,
                                        method: init?.method || "unspecified",
                                        requestInit: init
                                    }
                                }
                            });
                        }

                        parsed = JSON.parse(responseBody);
                    } catch (e) {
                        // Log the URL, truncated response, and some headers for Justin to debug the 200 error issue.
                        // This can be removed later if we figure out a solution.
                        console.error(`Response for ${apiUrl} couldn't be parsed: ${responseBody.slice(0, 50)}\n Content Encoding: ${result.headers.get("Content-Encoding")}\n Content Length: ${result.headers.get("Content-Length")}\n Method: ${init?.method}` );

                        const responseHeaders = Object.fromEntries(result.headers.entries());

                        // Send the full response including all headers to Sentry for debugging any parse issues
                        Sentry.captureException(e, {
                            extra: {
                                fullAPIResponse: {
                                    url: apiUrl,
                                    headers: responseHeaders,
                                    status: status_code,
                                    truncatedBody: responseBody?.slice(0, 100),
                                    bodyLength: responseBody?.length,
                                    method: init?.method || "unspecified",
                                    requestInit: init
                                }
                            }
                        });
                        throwAPIError(i18n.t("api-request.structure-error-message") + "- JSON Parsing", status_code, responseBody, undefined, {headers: responseHeaders});
                    }

                    if(typeof parsed !== "object") {
                        console.warn(`Response for ${apiUrl} is not an object: ${responseBody}` );
                        throwAPIError(i18n.t("api-request.structure-error-message" + "- Not an object"), status_code, parsed, undefined, {
                            headers: Object.fromEntries(result.headers.entries()),
                            rawBody: responseBody
                        });
                    }

                    if (status_code < 200 || status_code >= 500) {
                        throwAPIError(i18n.t("api-request.server-error-message"), status_code, parsed, parsed.error);
                    } else if (status_code >= 400) {
                        if (status_code === 401 || status_code === 403) {
                            // We have had some reports from clients receiving some strange 401/403
                            // errors.  Cloudflare logged the response as a HTTP 200 OK.  Hopefully
                            // this logging will give us some more information to investigate what
                            // is actually happening here.
                            Sentry.captureException("", {
                                extra: {
                                    fullAPIResponse: {
                                        url: apiUrl,
                                        headers: result.headers,
                                        status: status_code,
                                        truncatedBody: responseBody?.slice(0, 100),
                                        bodyLength: responseBody?.length,
                                        method: init?.method || "unspecified",
                                        requestInit: init
                                    }
                                }
                            });
                        }
                        if (status_code === 401) {
                            clearExpiredSession();
                            throwAPIError(i18n.t("api-request.authentication-error-message"), status_code, parsed, parsed.error);
                            if (!apiUrl.includes("login")) {
                                window.location.href = "/login";
                            }
                        } else {
                            throwAPIError(i18n.t("api-request.client-error-message"), status_code, parsed, parsed.error);
                        }
                    }

                    return parsed;
                } else return result;
            }
        } catch (reason: any) {
            if (typeof reason === 'object' && Object.hasOwn(reason, 'code')) {
                // This is our own exception! Re-throw it.
                APIRequestErrorCallbacks.forEach((fn: APIRequestErrorCallbackFn) => fn(reason));
                throw reason;
            } else if (status_code === 413) {
                // If our request body was too big, the API will return 413 and status text that says "Payload Too Large"
                // 413 occurs for a query-based API timeout (if a search takes too long), which will have a message indicating timeout
                throwAPIError(i18n.t("api-request.timeout-error-message"), status_code);
            } else if (status_code !== 0) {
                // We have a status code and got here, probably a parse error from something that wasn't valid JSON
                if (status_code === 200) {
                    // Server said 200, but something went wrong parsing
                    console.warn(`Response for ${apiUrl} couldn't be parsed: ${responseBody}`);
                    throwAPIError(i18n.t("api-request.structure-error-message") + "- Exception Caught", status_code, responseBody, undefined, {
                        exception: reason,
                        headers: result ? Object.fromEntries(result.headers.entries()) : undefined,
                        rawBody: responseBody
                    });
                } else {
                    // This likely means we got an error from our frontend/CDN, a mis-configured load balancer, or something else, and didn't reach the API itself
                    throwAPIError(i18n.t("api-request.parse-error-message"), status_code);
                }
            } else {
                // No status code
                // Something went really wrong - likely a client network issue
                if (retriesRemaining > 0) {
                  console.warn(endpoint, "No API response (0 status; connection problem?), will retry...");
                  retriesRemaining--;
                  await new Promise(r => setTimeout(r, 1000));
                  continue;
                } else {
                  throwAPIError(i18n.t("api-request.connection-error-message"), 0);
                }
            }
        }
    }
}

// Pass a portion of a URL or request body - any cache entries containing this piece will be removed
export function removeCachesMatching(urlPart: string) {
  apiQueryQueue.removeCachesMatching(urlPart);
}

export { apiRequestCacheExpiration };